import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setIsLoginDialog } from '../../redux/reducers/loginSlice';
import { Shield, FileText, Lock, Users, Globe, Clock, Baby, UserCheck, Radio, RefreshCw, Mail, Database } from 'lucide-react';
import ContactFormModal from '../ContactForm/ContactFormModal';

const PrivacyPolicy = () => {
    const dispatch = useDispatch();
    const [activeSection, setActiveSection] = useState('');
    const [openContactForm, setOpenContactForm] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(setIsLoginDialog(false));

        // Smooth scroll behavior
        document.documentElement.style.scrollBehavior = 'smooth';

        // Intersection Observer for active section highlighting
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-20% 0px -80% 0px' }
        );

        document.querySelectorAll('section[id]').forEach((section) => {
            observer.observe(section);
        });

        return () => {
            document.documentElement.style.scrollBehavior = '';
            observer.disconnect();
        };
    }, [dispatch]);

    const sections = [
        { id: 'summary', title: 'Summary', icon: FileText },
        { id: 'section-1', title: '1. Information We Collect', icon: Database },
        { id: 'section-2', title: '2. How We Process', icon: Shield },
        { id: 'section-3', title: '3. AI Model Training', icon: RefreshCw },
        { id: 'section-4', title: '4. Information Sharing', icon: Users },
        { id: 'section-5', title: '5. Cookies & Tracking', icon: Globe },
        { id: 'section-6', title: '6. Social Logins', icon: UserCheck },
        { id: 'section-7', title: '7. International Transfer', icon: Globe },
        { id: 'section-8', title: '8. Data Retention', icon: Clock },
        { id: 'section-9', title: '9. Minors', icon: Baby },
        { id: 'section-10', title: '10. Your Rights', icon: Lock },
        { id: 'section-11', title: '11. Do-Not-Track', icon: Radio },
        { id: 'section-12', title: '12. Updates', icon: RefreshCw },
        { id: 'section-13', title: '13. Contact Us', icon: Mail },
        { id: 'section-14', title: '14. Data Access', icon: Database },
    ];

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section - Matching website's dark theme */}
            <div className="relative overflow-hidden bg-deep-charcoal text-white">
                <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-deep-charcoal to-gray-900"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-6 py-20 md:py-32 relative z-10">
                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
                            <Shield className="w-4 h-4 text-emerald" />
                            <span className="text-sm font-semibold text-white">Your Privacy Matters</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight uppercase text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">
                            Privacy Notice
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed max-w-3xl">
                            We're committed to protecting your privacy and being transparent about how we handle your data.
                        </p>
                        <p className="mt-6 text-gray-400 text-sm">
                            Last updated: <span className="font-semibold text-white">1-Jan-2026 (Refined)</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-12 md:py-20">
                <div className="flex flex-col lg:flex-row gap-12 max-w-7xl mx-auto">
                    {/* Sticky Table of Contents */}
                    <aside className="lg:w-80 flex-shrink-0">
                        <div className="lg:sticky lg:top-24 space-y-6">
                            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
                                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <FileText className="w-5 h-5" style={{ color: '#247E45' }} />
                                    Table of Contents
                                </h2>
                                <nav className="space-y-1">
                                    {sections.map((section) => {
                                        const Icon = section.icon;
                                        return (
                                            <button
                                                key={section.id}
                                                onClick={() => scrollToSection(section.id)}
                                                className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-3 group ${activeSection === section.id
                                                    ? 'font-semibold'
                                                    : 'text-slate-600 hover:bg-gray-50 hover:text-gray-900'
                                                    }`}
                                                style={activeSection === section.id ? { backgroundColor: '#D9F0DE', color: '#026B33' } : {}}
                                            >
                                                <Icon className={`w-4 h-4 flex-shrink-0`}
                                                    style={{ color: activeSection === section.id ? '#026B33' : '#9CA3AF' }}
                                                />
                                                <span className="text-sm leading-tight">{section.title}</span>
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>

                            {/* Quick Contact Card */}
                            <div className="rounded-2xl p-6 border-2" style={{ background: 'linear-gradient(to bottom right, #D9F0DE, #E8FFE8)', borderColor: '#247E45' }}>
                                <h3 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#026B33' }}>
                                    <Mail className="w-5 h-5" />
                                    Questions?
                                </h3>
                                <p className="text-sm mb-3" style={{ color: '#026B33' }}>
                                    Contact us about this privacy notice
                                </p>
                                <button
                                    onClick={() => setOpenContactForm(true)}
                                    className="inline-flex items-center gap-2 text-sm font-semibold hover:underline transition-colors"
                                    style={{ color: '#026B33' }}
                                >
                                    Click Here
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </aside>

                    {/* Content */}
                    <main className="flex-1 min-w-0">
                        {/* Introduction */}
                        <div className="prose prose-slate max-w-none mb-12">
                            <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-8 md:p-10">
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    This privacy notice for <a href='https://www.visist.ai' target='_blank' rel='noopener noreferrer' className="font-semibold no-underline hover:underline" style={{ color: '#247E45' }}>www.visist.ai</a> ("we," "us," or "our"), describes how we collect, store, use, and share ("process") your information when you use our services ("Services").
                                </p>
                                <p className="text-gray-600 leading-relaxed">
                                    Questions or concerns? Reading this notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our Services.
                                </p>
                            </div>
                        </div>

                        {/* Summary Section */}
                        <section id="summary" className="mb-12 scroll-mt-24">
                            <div className="rounded-2xl shadow-sm p-8 md:p-10" style={{ background: 'linear-gradient(to bottom right, #D9F0DE, #FFFFFF)', border: '2px solid #247E45' }}>
                                <h2 className="text-3xl font-black text-gray-900 mb-6 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#247E45' }}>
                                        <FileText className="w-5 h-5 text-white" />
                                    </div>
                                    Summary of Key Points
                                </h2>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    We collect personal information you give us and certain data automatically. We may use your data, after anonymizing or aggregating it, to train and improve our AI models. You have rights to your data, but these are subject to our legal and business requirements.
                                </p>
                            </div>
                        </section>

                        {/* Section 1 */}
                        <section id="section-1" className="mb-12 scroll-mt-24">
                            <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-8 md:p-10 hover:border-emerald transition-all duration-300">
                                <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#D9F0DE' }}>
                                        <Database className="w-5 h-5" style={{ color: '#247E45' }} />
                                    </div>
                                    1. What Information Do We Collect?
                                </h2>

                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-lg font-bold mb-3" style={{ color: '#026B33' }}>Personal information you provide to us:</h3>
                                        <p className="text-gray-700 leading-relaxed">
                                            We collect personal information you voluntarily give us when you register, express interest in our Services, participate in activities, or contact us. This includes, but is not limited to, your name, contact information, and any content you submit or upload while using our Services. All personal information must be true, complete, and accurate.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-bold mb-3" style={{ color: '#026B33' }}>Information automatically collected:</h3>
                                        <p className="text-gray-700 leading-relaxed">
                                            We automatically collect certain data when you visit or use our Services. This may include your IP address, browser and device characteristics, operating system, language preferences, referring URLs, location, and information about how and when you use our Services.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Section 2 */}
                        <section id="section-2" className="mb-12 scroll-mt-24">
                            <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-8 md:p-10 hover:border-emerald transition-all duration-300">
                                <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#D9F0DE' }}>
                                        <Shield className="w-5 h-5" style={{ color: '#247E45' }} />
                                    </div>
                                    2. How Do We Process Your Information?
                                </h2>
                                <p className="text-gray-700 leading-relaxed">
                                    We process your personal information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with the law. We may also process your information for other purposes with your consent.
                                </p>
                            </div>
                        </section>

                        {/* Section 3 - AI Training (Highlighted) */}
                        <section id="section-3" className="mb-12 scroll-mt-24">
                            <div className="rounded-2xl shadow-md p-8 md:p-10" style={{ background: 'linear-gradient(to bottom right, #E0F2FE, #FFFFFF)', border: '2px solid #3B82F6' }}>
                                <div className="flex items-start gap-3 mb-4">
                                    <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <RefreshCw className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">
                                            3. Data Use for Training and Improving Models
                                        </h2>
                                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                                            Fortified Term
                                        </span>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        You acknowledge and agree that we may use the information collected from you (including content and usage data) to develop, train, and improve our products and Services, including our artificial intelligence and machine learning models.
                                    </p>
                                    <p className="text-gray-700 leading-relaxed">
                                        To protect your privacy, we will first anonymize, aggregate, or de-identify your personal information and usage data before using it for model training purposes. We retain the right to use any such anonymized, aggregated, or de-identified data indefinitely for our business purposes.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Section 4 */}
                        <section id="section-4" className="mb-12 scroll-mt-24">
                            <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-8 md:p-10 hover:border-emerald transition-all duration-300">
                                <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#D9F0DE' }}>
                                        <Users className="w-5 h-5" style={{ color: '#247E45' }} />
                                    </div>
                                    4. When and With Whom Do We Share Your Personal Information?
                                </h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    We may share your personal information in specific situations and/or with the following third parties:
                                </p>
                                <ul className="space-y-3">
                                    <li className="flex gap-3">
                                        <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                                        <div>
                                            <strong className="text-gray-900">Business Transfers:</strong>
                                            <span className="text-gray-700"> In connection with or during negotiations of any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</span>
                                        </div>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                                        <div>
                                            <strong className="text-gray-900">Affiliates:</strong>
                                            <span className="text-gray-700"> With our corporate affiliates, including our parent company and subsidiaries.</span>
                                        </div>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                                        <div>
                                            <strong className="text-gray-900">Business Partners:</strong>
                                            <span className="text-gray-700"> With our business partners to offer you certain products, services, or promotions.</span>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </section>

                        {/* Sections 5-14 */}
                        {[
                            {
                                id: 'section-5',
                                icon: Globe,
                                title: '5. Do We Use Cookies and Other Tracking Technologies?',
                                content: 'Yes, we use cookies and similar tracking technologies to collect and store your information. Specific details on how we use these and how you can refuse certain cookies are in our separate Cookie Notice.'
                            },
                            {
                                id: 'section-6',
                                icon: UserCheck,
                                title: '6. How Do We Handle Your Social Logins?',
                                content: 'If you register or log in using a social media account, we may have access to certain information about you. We will use this information only for the purposes described in this privacy notice.'
                            },
                            {
                                id: 'section-7',
                                icon: Globe,
                                title: '7. Is Your Information Transferred Internationally?',
                                content: 'We may transfer, store, and process your information in countries other than your own. If you are accessing our Services from outside India, your information may be transferred to, stored, and processed by us and our third-party service providers in India and other countries.'
                            },
                            {
                                id: 'section-8',
                                icon: Clock,
                                title: '8. How Long Do We Keep Your Information (Data Storage and Access)?',
                                content: 'We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy notice, as determined by us and unless a longer retention period is required or permitted by law.'
                            },
                            {
                                id: 'section-9',
                                icon: Baby,
                                title: '9. Do We Collect Information From Minors?',
                                content: 'We do not knowingly collect data from or market to children under 18 years of age. By using the Services, you represent that you are at least 18 years old or are the parent or guardian of a minor using the Services with your consent.'
                            },
                            {
                                id: 'section-10',
                                icon: Lock,
                                title: '10. What Are Your Privacy Rights?',
                                content: 'You may review, change, or terminate your account at any time. If we are relying on your consent to process your personal information, you have the right to withdraw your consent at any time.'
                            },
                            {
                                id: 'section-11',
                                icon: Radio,
                                title: '11. Controls for Do-Not-Track Features',
                                content: 'We do not currently respond to Do-Not-Track browser signals or any other mechanism that automatically communicates your choice not to be tracked online.'
                            },
                            {
                                id: 'section-12',
                                icon: RefreshCw,
                                title: '12. Do We Make Updates to This Notice?',
                                content: 'Yes, we may update this privacy notice from time to time to stay compliant with relevant laws and reflect changes in our business.'
                            },
                            {
                                id: 'section-13',
                                icon: Mail,
                                title: '13. How Can You Contact Us About This Notice?',
                                content: (
                                    <>
                                        If you have questions or comments about this notice, you may contact us by post at:{' '}
                                        <a href="https://mail.google.com/mail/?view=cm&fs=1&to=info@visist.ai" target="_blank" rel="noopener noreferrer" className="font-semibold no-underline hover:underline" style={{ color: '#247E45' }}>
                                            info@visist.ai
                                        </a>
                                    </>
                                )
                            },
                            {
                                id: 'section-14',
                                icon: Database,
                                title: '14. How Can You Review, Update, or Delete the Data We Collect From You (Data Storage and Access)?',
                                content: 'Based on the applicable laws of India, you may have the right to request access to the personal information we collect from you, change that information, or delete it. To request to review, update, or delete your personal information, please fill out and submit a data subject access request. All requests are subject to verification, our internal policies, and any overriding legal or regulatory requirements that compel us to retain certain information.'
                            }
                        ].map((section) => {
                            const Icon = section.icon;
                            return (
                                <section key={section.id} id={section.id} className="mb-12 scroll-mt-24">
                                    <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-8 md:p-10 hover:border-emerald transition-all duration-300">
                                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6 flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#D9F0DE' }}>
                                                <Icon className="w-5 h-5" style={{ color: '#247E45' }} />
                                            </div>
                                            {section.title}
                                        </h2>
                                        <p className="text-gray-700 leading-relaxed">
                                            {section.content}
                                        </p>
                                    </div>
                                </section>
                            );
                        })}

                        {/* Footer CTA - Matching website's dark theme */}
                        <div className="rounded-2xl p-8 md:p-10 text-white text-center relative overflow-hidden bg-deep-charcoal">
                            <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-gray-900 to-deep-charcoal"></div>
                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald/10 rounded-full blur-3xl"></div>
                            <div className="relative z-10">
                                <h3 className="text-2xl font-bold mb-3 text-white">Still Have Questions?</h3>
                                <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                                    We're here to help. Reach out to us if you need clarification on any part of this privacy notice.
                                </p>
                                <button
                                    onClick={() => setOpenContactForm(true)}
                                    className="inline-flex items-center gap-2 px-8 py-4 bg-white font-bold rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                                    style={{ color: '#026B33' }}
                                >
                                    <Mail className="w-5 h-5" />
                                    Contact Us
                                </button>
                            </div>
                        </div>
                    </main>
                </div>
            </div >

            {/* Contact Form Modal */}
            < ContactFormModal
                isOpen={openContactForm}
                onClose={() => setOpenContactForm(false)}
            />
        </div >
    );
};

export default PrivacyPolicy;
